"use client"

import { useState, useCallback, useRef, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import type { UserMenuAction } from "../types/header"
import { HEADER_CONSTANTS } from "../constants/header"

export const useUserMenu = () => {
  const [showUserMenu, setShowUserMenu] = useState(false)
  const userMenuRef = useRef<HTMLDivElement | null>(null)
  const navigate = useNavigate()
  const { logout } = useAuth()

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }

    if (showUserMenu) {
      document.addEventListener("mousedown", handleClickOutside)
      return () => document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [showUserMenu])

  const toggleUserMenu = useCallback(() => {
    setShowUserMenu((prev) => !prev)
  }, [])

  const closeUserMenu = useCallback(() => {
    setShowUserMenu(false)
  }, [])

  const handleProfileClick = useCallback(() => {
    navigate(HEADER_CONSTANTS.ROUTES.PROFILE)
    closeUserMenu()
  }, [navigate, closeUserMenu])

  const handleDashBoardClick = useCallback(() => {
    navigate(HEADER_CONSTANTS.ROUTES.DASHBOARD)
    closeUserMenu()
  }, [navigate, closeUserMenu])

  const handleSettingsClick = useCallback(() => {
    navigate(HEADER_CONSTANTS.ROUTES.SETTINGS)
    closeUserMenu()
  }, [navigate, closeUserMenu])

  const handleLogout = useCallback(async () => {
    try {
      await logout()
      navigate(HEADER_CONSTANTS.ROUTES.LOGIN)
      closeUserMenu()
    } catch (error) {
      console.error("Logout failed:", error)
    }
  }, [logout, navigate, closeUserMenu])

  const userMenuActions: UserMenuAction[] = [
    {
      label: "Dashboard",
      icon: (
        <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M3 9L12 2l9 7" />
    <path d="M9 22V12h6v10" />
    <path d="M21 22H3" />
  </svg>
      ),
      onClick: handleDashBoardClick,
    },
    {
      label: "Profile",
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      ),
      onClick: handleProfileClick,
    },
    // {
    //   label: "Settings",
    //   icon: (
    //     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    //       <circle cx="12" cy="12" r="3" />
    //       <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    //     </svg>
    //   ),
    //   onClick: handleSettingsClick,
    // },
    {
      label: "Logout",
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16,17 21,12 16,7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
      ),
      onClick: handleLogout,
      variant: "danger" as const,
    },
  ]

  return {
    showUserMenu,
    userMenuRef,
    toggleUserMenu,
    closeUserMenu,
    userMenuActions,
  }
}
